import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Typography, message, Table, Pagination, Dropdown, Tooltip, Button } from 'antd';
import type { MenuProps } from 'antd';
import {
  MoreOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  LoadingOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import ChangeLogButton from '../changeLog/ChangeLogButton';
import ExportButton from '../../../components/common/ExportButton';
import FilterButton from '../../../components/common/FilterButton';
import type { ColumnsType } from 'antd/es/table';
import LinkIcon from '../../../assets/icons/ic-link.svg?react';
import EditIcon from '../../../assets/icons/ic-edit.svg?react';
import TrashIcon from '../../../assets/icons/ic-trash.svg?react';
import ButtonGroup from '../../../components/common/ButtonGroup';
import ConfirmModal from '../../../components/common/ConfirmModal';
import AddItemButton from '../../../components/common/AddItemButton';
import RightContentDrawer from '../../../components/common/RightContentDrawer';
import SortableColumnHeader from '../../../components/common/SortableColumnHeader';
import ControlsFilterDrawer, { ControlsFilterState, CompletedStatus } from './ControlsFilterDrawer';
import { useTableSort } from '../../../hooks';
import { RootState, AppDispatch } from '../../../store';
import {
  getControlActivities,
  ControlActivity,
  updateControl,
  UpdateControlRequest,
  frequencyToApiEnum,
  convertEffectiveness,
  convertInterimCompleted,
  convertFinalCompleted,
  deleteControl,
  EffectivenessStatus,
} from '../../../services/controlActivitiesService';
import {
  setControlActivities,
  setCurrentPage,
  setPageSize as setPageSizeAction,
} from '../../../store/slices/controlActivitiesSlice';
import { clearHierarchyData } from '../../../store/slices/hierarchySlice';
import { setGlobalPageSize } from '../../../store/slices/generalSlice';
import { downloadFile } from '../../../services/fileService';
import warningIcon from '../../../assets/icons/ic-warning.svg';
import { useNavigate, useLocation } from 'react-router-dom';
import ExportPanel, { ExportColumnConfig } from '../../../components/common/ExportPanel';
import { RiskRanking } from '@/services/types';

const { Title } = Typography;

// ==================== STYLED COMPONENTS ====================

const PageContainer = styled.div`
  padding: 0;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const PageTitle = styled(Title)`
  margin: 0 !important;
  font-family: Inter !important;
  font-weight: 500 !important;
  font-size: 18px !important;
  line-height: 100% !important;
  letter-spacing: 0.005em !important;
  color: #1F2937 !important;
`;


const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const TableWrapper = styled.div`
  position: relative;
`;

const TableContainer = styled.div`
  background: white;
  border-radius: 6px;
  border: 1px solid #E5E7EB;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  width: 100%;
  overflow: hidden;
  
  .ant-table-wrapper {
    overflow-x: auto;
    max-width: 100%;
  }
  
  .ant-table {
    font-size: 14px;
    border: none;
  }
  
  .ant-table-thead > tr > th {
    background: #F8FAFC;
    font-family: Inter;
    font-weight: 500;
    color: #6B7280;
    font-size: 14px;
    padding: 16px 12px;
    border-bottom: 1px solid #E5E7EB;
    border-right: 1px solid #E5E7EB;
    white-space: nowrap;
  
    &:last-child {
      border-right: none;
    }

    &::before {
      display: none !important;
    }
  }

  .ant-table-tbody > tr > td {
    padding: 20px 12px;
    font-size: 14px;
    color: #1F2937;
    border-bottom: 1px solid #E5E7EB;
    border-right: 1px solid #E5E7EB;
    vertical-align: middle;
    background: white;
  
    &:last-child {
      border-right: none;
    }
  }
  
  .ant-table-tbody > tr:hover > td {
    background: #F9FAFB;
  }
  
  /* Remove selection highlight */
  .ant-table-tbody > tr.ant-table-row-selected > td,
  .ant-table-tbody > tr.ant-table-row:active > td {
    background: transparent !important;
  }

  /* Sticky column styles */
  .ant-table-cell-fix-left {
    background: white !important;
    z-index: 2;
  }

  .ant-table-thead > tr > th.ant-table-cell-fix-left {
    background: #F8FAFC !important;
  }

  .ant-table-tbody > tr:hover > td.ant-table-cell-fix-left {
    background: #F9FAFB !important;
  }
`;

const PaginationContainer = styled.div`
  padding: 16px 24px;
  display: flex;
  justify-content: flex-start;
  border-top: 1px solid #E5E7EB;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 6px;
`;

const LoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #E5E7EB;
    border-top-color: #2563EB;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  .text {
    font-family: Inter;
    font-weight: 500;
    font-size: 14px;
    color: #6B7280;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
`;

const EmptyStateMessage = styled.p`
  color: #6B7280;
  font-size: 16px;
  margin-bottom: 24px;
`;

// ==================== CHIP COMPONENTS ====================

const StatusChip = styled.span<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 500;
  background: ${props => {
    switch (props.$status) {
      case 'Active': return '#4AB7B6';
      case 'Draft': return '#E5B454';
      default: return '#F3F4F6';
    }
  }};
  color: white;
  
  &::before {
    content: '●';
    font-size: 10px;
  }
`;

const CycleChip = styled.span`
  display: inline-block;
  padding: 8px 16px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 500;
  background: #ECFDF5;
  color: #047857;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  
  &:hover {
    opacity: 0.8;
  }
`;

const ProcessChip = styled.span`
  display: inline-block;
  padding: 8px 16px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 500;
  background: #EEF2FF;
  color: #3730A3;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  
  &:hover {
    opacity: 0.8;
  }
`;

const RiskRankingChip = styled.span<{ $level: string }>`
  display: inline-block;
  padding: 8px 16px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 500;
  cursor: default;
  background: white;

  ${props => {
    switch (props.$level) {
      case 'High':
        return `
          border: 1px solid #EF4444;
          color: #EF4444;
        `;
      case 'Med':
        return `
          border: 1px solid #FACC15;
          color: #FACC15;
        `;
      case 'Low':
        return `
          border: 1px solid #2DD4BF;
          color: #2DD4BF;
        `;
      default:
        return `
          border: 1px solid #60A5FA;
          color: #60A5FA;
        `;
    }
  }}

  &::before {
    content: '●';
    margin-right: 6px;
    font-size: 10px;
  }
`;

// Tooltip components for Risk Ranking objectives
const TooltipTitle = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: #1F2937;
  margin-bottom: 8px;
`;

const TooltipList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const TooltipItem = styled.div`
  padding: 6px 10px;
  background: #EFF6FF;
  border-radius: 6px;
  font-family: Inter;
  font-weight: 500;
  font-size: 13px;
  line-height: 18px;
  color: #2563EB;
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;

  &:hover {
    background: #DBEAFE;
  }
`;

const RiskTooltipTitle = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: #1F2937;
  text-align: center;
  margin-bottom: 12px;
`;

const RiskTooltipList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RiskTooltipItem = styled.div`
  padding: 8px 12px;
  background: #EFF6FF;
  border-radius: 6px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #1E40AF;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background: #DBEAFE;
    transform: translateX(2px);
  }
`;

const ControlOwnerText = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #1F2937;
`;

const ProcessOwnerContainer = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: #F8FAFC;
  border-radius: 50px;
`;

const ProcessOwnerAvatar = styled.span`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #F97316;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 8px;
  font-weight: 600;
`;

const ProcessOwnerName = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #1F2937;
`;

const ApplicationChip = styled.span`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 500;
  background: white;
  color: #374151;
  border: 1px solid #E5E7EB;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  flex-shrink: 0;
  
  &:hover {
    background: #F9FAFB;
  }
`;

const AssertionChip = styled.span`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.2s;
  background: #FEF9C3;
  color: #A16207;
  white-space: nowrap;
  flex-shrink: 0;
  
  &:hover {
    opacity: 0.9;
  }
`;

const TestingScopeChip = styled.span<{ $inScope: boolean; $expanded?: boolean; $clickable?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 500;
  background: white;
  color: ${props => props.$inScope ? '#2DD4BF' : '#9CA3AF'};
  border: 1px solid ${props => props.$inScope ? '#2DD4BF' : '#E5E7EB'};
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};
  transition: all 0.2s;
  white-space: nowrap;
  
  ${props => props.$clickable && `
    &:hover {
      opacity: 0.8;
    }
  `}
`;

const ExpandIcon = styled.span<{ $expanded: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  transition: transform 0.2s;
  transform: rotate(${props => props.$expanded ? '90deg' : '0deg'});
`;

const ControlIdChip = styled.span`
  display: inline-block;
  padding: 8px 16px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 500;
  background: #EEF2FF;
  color: #3730A3;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    opacity: 0.8;
  }
`;

const DescriptionText = styled.div`
  font-size: 14px;
  color: #1F2937;
  
  .what-label {
    font-weight: 700;
    color: #1F2937;
  }
  
  .what-text {
    font-weight: 400;
  color: #6B7280;
  }
  
  .when-text {
    font-size: 13px;
    color: #9CA3AF;
    margin-top: 8px;
  }
`;

const GrayChip = styled.span`
  display: inline-block;
  padding: 8px 16px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 500;
  background: white;
  color: #374151;
  border: 1px solid #E5E7EB;
`;

const TruncatedText = styled.span<{ $maxWidth?: string }>`
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: ${props => props.$maxWidth || '150px'};
  cursor: pointer;
    color: #2563EB;
  line-height: 1.4;
`;

const DesignEffectiveChip = styled.span<{ $value: string }>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => {
    switch (props.$value) {
      case 'Yes': return '#DCFCE7';
      case 'No': return '#FEE2E2';
      default: return '#F3F4F6';
    }
  }};
  color: ${props => {
    switch (props.$value) {
      case 'Yes': return '#16A34A';
      case 'No': return '#DC2626';
      default: return '#6B7280';
    }
  }};
`;

const CompletionChip = styled.span<{ $value: string }>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => {
    switch (props.$value) {
      case 'Completed':
      case 'Yes': return '#DCFCE7';  // Green
      case 'WIP': return '#FEF3C7';  // Yellow
      case 'Not Started':
      case 'No': return '#FEE2E2';   // Red
      default: return '#F3F4F6';     // Gray (N/A)
    }
  }};
  color: ${props => {
    switch (props.$value) {
      case 'Completed':
      case 'Yes': return '#16A34A';  // Green
      case 'WIP': return '#D97706';  // Yellow
      case 'Not Started':
      case 'No': return '#DC2626';   // Red
      default: return '#6B7280';     // Gray (N/A)
    }
  }};
`;

const AttachmentLink = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  background: #EFF6FF;
  color: #1E40AF;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
  text-decoration: none;
  margin-right: 8px;
  margin-bottom: 4px;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    opacity: 0.8;
  }

  svg {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
  }
`;

// Attachment link component with download and loading state
const AttachmentLinkWithDownload: React.FC<{
  fileId: string;
  fileName: string;
}> = ({ fileId, fileName }) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isDownloading) return;
    
    setIsDownloading(true);
    try {
      await downloadFile(fileId, fileName);
    } catch (error) {
      console.error('Failed to download file:', error);
      message.error('Failed to download file');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <AttachmentLink onClick={handleDownload}>
      {isDownloading ? (
        <LoadingOutlined style={{ fontSize: 12 }} spin />
      ) : (
        <LinkIcon />
      )}
      {fileName}
    </AttachmentLink>
  );
};

// ==================== ACTION MENU COMPONENTS ====================

const ActionMenuButton = styled.button`
  background: #F8FAFC;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  padding: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #6B7280;
  transition: all 0.3s;

  &:hover {
    color: #1F2937;
    background: #EFF6FF;
  }

  .anticon {
    font-size: 20px;
  }
`;

const MenuItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
  }
`;

const MenuItemText = styled.span<{ $danger?: boolean }>`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.005em;
  color: ${props => props.$danger ? '#EF4444' : '#1F2937'};
`;

// ==================== DRAWER COMPONENTS ====================

const DrawerContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const DrawerTextArea = styled.textarea`
  flex: 1;
  width: 100%;
  padding: 12px;
  font-family: Inter, sans-serif;
  font-size: 14px;
  line-height: 1.6;
  color: #1F2937;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  resize: none;
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: #2563EB;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
  }

  &::placeholder {
    color: #9CA3AF;
  }
`;


// ==================== HELPER FUNCTIONS ====================

// Build UpdateControlRequest from control data with optional overrides
const buildUpdateControlRequest = (
  control: ControlActivity,
  overrides?: { testPlan?: string; walthroughResult?: string }
): UpdateControlRequest => ({
  controlId: control.id,
  weight: control.controlWeight ?? 0,
  what: control.controlDescription.what,
  who: control.controlDescription.who,
  why: control.controlDescription.why,
  how: control.controlDescription.how,
  evidence: control.controlDescription.evidence,
  category: (control.controlCategory?.toLowerCase() || 'financial') as 'financial' | 'operational' | 'regulatory',
  controlType: (control.controlType?.toLowerCase() || 'preventative') as 'preventative' | 'detective' | 'other',
  controlFrequency: frequencyToApiEnum(control.frequency),
  isKeyControl: control.keyOrNonKey === 'Key',
  controlOwner: control.controlOwner,
  isGapCompensating: control.isGapCompensating,
  processId: control.process.id,
  inTestingScope: control.testingScope,
  testPlan: overrides?.testPlan ?? control.testing?.testPlan ?? '',
  walthroughResult: overrides?.walthroughResult ?? control.testing?.walkthroughResult ?? '',
  isDesignEffective: convertEffectiveness(control.testing?.designEffective),
  samplesTotal: control.testing?.totalTestSamples || 0,
  samplesTested: control.testing?.samplesTested || 0,
  documentIds: control.testing?.attachments?.map(att => att.id) || [],
  objectiveIds: control.objectiveIds || [],
  applicationIds: control.applications?.map(app => app.id) || [],
  interimCompleted: convertInterimCompleted(control.testing?.interimCompleted),
  finalCompleted: convertFinalCompleted(control.testing?.finalCompleted),
  operatingEffectiveness: convertEffectiveness(control.testing?.operatingEffectiveness),
  auditorId: control.testing?.auditorAssigned?.id,
  budgetHours: control.testing?.budgetHoursToTest ?? undefined,
});

// ==================== MAIN COMPONENT ====================

// Location state interface for navigation
interface LocationState {
  processId?: string | null;
  processCode?: string | null;
  processName?: string | null;
  cycleId?: string | null;
  cycleCode?: string | null;
  cycleName?: string | null;
  isKey?: boolean;
  effectivenessStatus?: string | null;
  testingScope?: boolean;
  hasAuditorAssigned?: boolean;
  interimStatus?: string;
  finalStatus?: string;
  riskRanking?: string;
  processOwnerId?: string | null;
  processOwnerName?: string | null;
}

const ControlActivitiesPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const [isLoading, setIsLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Get filter params from navigation state
  const locationState = location.state as LocationState | null;
  const processId = locationState?.processId ?? null;
  const processCode = locationState?.processCode ?? null;
  const processName = locationState?.processName ?? null;
  const cycleId = locationState?.cycleId ?? null;
  const cycleCode = locationState?.cycleCode ?? null;
  const cycleName = locationState?.cycleName ?? null;
  const isKey = locationState?.isKey ?? undefined;
  const effectivenessStatus = locationState?.effectivenessStatus ?? undefined;
  const testingScope = locationState?.testingScope ?? undefined;
  const hasAuditorAssigned = locationState?.hasAuditorAssigned ?? undefined;
  const interimStatus = locationState?.interimStatus ?? undefined;
  const finalStatus = locationState?.finalStatus ?? undefined;
  const riskRanking = locationState?.riskRanking ?? undefined;
  const processOwnerId = locationState?.processOwnerId ?? null;
  const processOwnerName = locationState?.processOwnerName ?? null;
  const hasNavigationParams = !!processId || !!cycleId || isKey !== undefined || !!effectivenessStatus || testingScope !== undefined || hasAuditorAssigned !== undefined || interimStatus !== undefined || finalStatus !== undefined || riskRanking !== undefined || !!processOwnerId;

  // Drawer states for Test Plan
  const [testPlanDrawerOpen, setTestPlanDrawerOpen] = useState(false);
  const [testPlanContent, setTestPlanContent] = useState('');
  const [testPlanControlId, setTestPlanControlId] = useState<string | null>(null);
  const [isUpdatingTestPlan, setIsUpdatingTestPlan] = useState(false);

  // Drawer states for Walkthrough Result
  const [walkthroughDrawerOpen, setWalkthroughDrawerOpen] = useState(false);
  const [walkthroughContent, setWalkthroughContent] = useState('');
  const [walkthroughControlId, setWalkthroughControlId] = useState<string | null>(null);
  const [isUpdatingWalkthrough, setIsUpdatingWalkthrough] = useState(false);

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingControl, setDeletingControl] = useState<ControlActivity | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Export panel state
  const [exportPanelOpen, setExportPanelOpen] = useState(false);

  const { sortState, handleSort: baseHandleSort, sortedParam } = useTableSort({ defaultField: 'ControlCode', defaultDirection: 'ascending' });

  // Sort loading state (only shows loading on sort icon, not full screen)
  const [isSortLoading, setIsSortLoading] = useState(false);
  // Ref to track previous sortedParam for detecting sort changes
  const prevSortedParamRef = useRef<string | null>(null);

  // Filter drawer state - single source of truth for all filters
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [filters, setFilters] = useState<ControlsFilterState>({});
  // Filter loading state (shows spinner on filter button when applying)
  const [isFilterLoading, setIsFilterLoading] = useState(false);

  // Get hierarchy data from Redux (for looking up cycle/process info)
  const { cycles, processesByCycleId } = useSelector((state: RootState) => state.hierarchy);

  // Sync navigation params to filters and reset navigation state
  // This runs once when navigating with params, builds filters, then clears navigation state
  useEffect(() => {
    if (hasNavigationParams) {
      const newFilters: ControlsFilterState = {};
      
      // Map navigation isKey to drawer filter
      if (isKey !== undefined) {
        newFilters.keyOrNonKey = isKey ? 'Key' : 'Non-Key';
      }
      
      if (effectivenessStatus) {
        newFilters.effectivenessStatus = [effectivenessStatus as EffectivenessStatus];
      }

      if (interimStatus) {
        newFilters.interimCompleted = [interimStatus as CompletedStatus];
      }

      if (finalStatus) {
        newFilters.finalCompleted = [finalStatus as CompletedStatus];
      }
      
      if (testingScope !== undefined) {
        newFilters.testingScope = testingScope;
      }
      
      if (hasAuditorAssigned !== undefined) {
        newFilters.hasAuditorAssigned = hasAuditorAssigned;
      }
      
      if (riskRanking) {
        newFilters.riskRanking = [riskRanking as RiskRanking];
      }
      
      // Map cycleId to drawer filter with full cycle info
      if (cycleId) {
        // Use cycle info from navigation state if available
        if (cycleCode && cycleName) {
          newFilters.cycles = [{
            cycleId: cycleId,
            cycleCode: cycleCode,
            cycleName: cycleName,
          }];
        } else {
          // Fallback: try to find from Redux
          const cycle = cycles.find(c => c.id === cycleId);
          if (cycle) {
            newFilters.cycles = [{
              cycleId: cycle.id,
              cycleCode: cycle.cycleCode,
              cycleName: cycle.cycleName,
            }];
          } else {
            // Last fallback: use cycleId as both id and code
            newFilters.cycles = [{
              cycleId: cycleId,
              cycleCode: cycleId,
              cycleName: '',
            }];
          }
        }
      }
      
      // Map processId to drawer filter with full process info
      if (processId) {
        // Use process info from navigation state if available
        if (processCode && processName) {
          newFilters.processes = [{
            processId: processId,
            processCode: processCode,
            processName: processName,
            riskAssessmentId: '',
            processOwnerId: '',
            processOwnerName: '',
          }];
        } else if (cycleId) {
          // Fallback: try to find from Redux
          const processesForCycle = processesByCycleId[cycleId] || [];
          const process = processesForCycle.find(p => p.id === processId);
          if (process) {
            newFilters.processes = [{
              processId: process.id,
              processCode: process.processCode,
              processName: process.processName,
              riskAssessmentId: process.riskAssessmentId || '',
              processOwnerId: process.processOwnerId || '',
              processOwnerName: process.processOwner || '',
            }];
          } else {
            // Last fallback: use processId as both id and code
            newFilters.processes = [{
              processId: processId,
              processCode: processId,
              processName: '',
              riskAssessmentId: '',
              processOwnerId: '',
              processOwnerName: '',
            }];
          }
        } else {
          // Last fallback: use processId as both id and code
          newFilters.processes = [{
            processId: processId,
            processCode: processId,
            processName: '',
            riskAssessmentId: '',
            processOwnerId: '',
            processOwnerName: '',
          }];
        }
      }

      if (processOwnerId) {
        newFilters.processOwners = [{
          userId: processOwnerId,
          userName: processOwnerName || '',
          title: '',
        }];
      }
      
      setFilters(newFilters);
      
      // Reset navigation state immediately after building filters
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [
    processId,
    processCode,
    processName,
    cycleId,
    cycleCode,
    cycleName,
    isKey,
    effectivenessStatus,
    testingScope,
    hasAuditorAssigned,
    interimStatus,
    finalStatus,
    riskRanking,
    processOwnerId,
    processOwnerName,
    cycles,
    processesByCycleId,
    navigate,
    location.pathname
  ]);

  // Get current annual report from Redux
  const { selectedReport } = useSelector((state: RootState) => state.annualReport);

  // Get control activities data from Redux
  const {
    controlActivities,
    totalCount,
    lastFetchedReportId,
    currentPage,
    pageSize
  } = useSelector((state: RootState) => state.controlActivities);

  // Get isAdmin flag from Redux (computed in authSlice)
  const { isAdmin, isExternalUser } = useSelector((state: RootState) => state.auth);

  // Sync local pageSize with global pageSize
  const { globalPageSize } = useSelector((state: RootState) => state.general);
  useEffect(() => {
    if (globalPageSize !== pageSize) {
      dispatch(setPageSizeAction(globalPageSize));
    }
  }, [globalPageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  // Wrap handleSort to set loading immediately when clicked
  const handleSort = useCallback((field: string) => {
    // Only set loading if we have data (not initial load)
    if (controlActivities.length > 0) {
      setIsSortLoading(true);
    }
    baseHandleSort(field);
  }, [baseHandleSort, controlActivities.length]);

  // Export columns configuration - includes control details and testing columns
  const exportColumns: ExportColumnConfig[] = useMemo(() => [
    // Control Details
    { key: 'controlCode', title: 'Control ID', width: 10 },
    { key: 'status', title: 'Status', width: 10 },
    { key: 'cycle', title: 'Cycle', width: 15 },
    { key: 'process', title: 'Process', width: 15 },
    { key: 'controlDescription', title: 'Control Description', width: 50 },
    { key: 'controlCategory', title: 'Category', width: 12 },
    { key: 'controlType', title: 'Type', width: 12 },
    { key: 'frequency', title: 'Frequency', width: 10 },
    { key: 'keyOrNonKey', title: 'Key/Non-Key', width: 10 },
    { key: 'riskRanking', title: 'Risk Ranking', width: 10 },
    { key: 'controlWeight', title: 'Weight', width: 8 },
    { key: 'controlOwner', title: 'Control Owner', width: 15 },
    { key: 'processOwner', title: 'Process Owner', width: 15 },
    { key: 'applications', title: 'Applications', width: 15 },
    { key: 'assertions', title: 'Assertions', width: 12 },
    { key: 'objectives', title: 'Objectives', width: 15 },
    // Testing Details
    { key: 'testingScope', title: 'In Scope', width: 8 },
    { key: 'designEffective', title: 'Design Effective', width: 12 },
    { key: 'operatingEffectiveness', title: 'Operating Effective', width: 12 },
    { key: 'testingStatus', title: 'Testing %', width: 10 },
    { key: 'totalTestSamples', title: 'Total Samples', width: 10 },
    { key: 'samplesTested', title: 'Samples Tested', width: 10 },
    { key: 'interimCompleted', title: 'Interim', width: 10 },
    { key: 'finalCompleted', title: 'Final', width: 10 },
    { key: 'auditorAssigned', title: 'Auditor', width: 15 },
  ], []);

  // Export data - flatten control activities for export (includes testing)
  const exportData = useMemo(() => 
    controlActivities.map(control => ({
      // Control Details
      controlCode: control.controlCode,
      status: control.status || '',
      cycle: `${control.cycle.code} ${control.cycle.name}`,
      process: `${control.process.code} ${control.process.name}`,
      controlDescription: [
        `What - ${control.controlDescription.what || ''}`,
        `When - ${control.controlDescription.when || ''}`,
        `Who - ${control.controlDescription.who || ''}`,
        `Why - ${control.controlDescription.why || ''}`,
        `How - ${control.controlDescription.how || ''}`,
        `Evidence - ${control.controlDescription.evidence || ''}`,
      ].join('\n\n'),
      controlCategory: control.controlCategory || '',
      controlType: control.controlType || '',
      frequency: control.frequency || '',
      keyOrNonKey: control.keyOrNonKey || '',
      riskRanking: control.riskRanking || '',
      controlWeight: control.controlWeight ?? '',
      controlOwner: control.controlOwner || '',
      processOwner: control.processOwner?.name || '',
      applications: control.applications?.map(app => app.name).join(', ') || '',
      assertions: control.assertions?.join(', ') || '',
      objectives: control.objectiveDisplayTitles?.join(', ') || '',
      // Testing Details
      testingScope: control.testingScope ? 'Yes' : 'No',
      designEffective: control.testing?.designEffective || '-',
      operatingEffectiveness: control.testing?.operatingEffectiveness || '-',
      testingStatus: control.testing?.testingStatus != null ? `${control.testing.testingStatus}%` : '-',
      totalTestSamples: control.testing?.totalTestSamples ?? '-',
      samplesTested: control.testing?.samplesTested ?? '-',
      interimCompleted: control.testing?.interimCompleted || '-',
      finalCompleted: control.testing?.finalCompleted || '-',
      auditorAssigned: control.testing?.auditorAssigned?.name || '-',
    }))
  , [controlActivities]);

  // Ref to prevent concurrent fetches (fixes double API call issue)
  const isFetchingRef = useRef(false);

  // Fetch control activities when report changes or filters/sort changes
  useEffect(() => {
    const fetchData = async () => {
      if (!selectedReport?.annualReportId) return;

      // Skip fetch if navigation params exist - let navigation sync useEffect handle it first
      if (hasNavigationParams) {
        console.warn('⏳ Waiting for navigation params to sync...');
        return;
      }

      // Prevent concurrent fetches
      if (isFetchingRef.current) {
        console.warn('⏳ Already fetching, skipping...');
        return;
      }

      isFetchingRef.current = true;
      
      // Create current sort key
      const currentSortKey = sortedParam ? `${sortedParam[0]?.name}-${sortedParam[0]?.direction}` : 'default';
      
      // Check if this is initial load or data change (not sort-only)
      const isInitialOrDataChange = prevSortedParamRef.current === null || controlActivities.length === 0;
      
      // Update the ref with current sort key
      prevSortedParamRef.current = currentSortKey;
      
      // Set loading states
      if (isInitialOrDataChange) {
        setIsLoading(true);
      }
      setIsFilterLoading(true);

      try {
        const response = await getControlActivities({
          annualReportId: selectedReport.annualReportId,
          sorted: sortedParam,
          status: filters.status,
          cycleIds: filters.cycles?.map(c => c.cycleId),
          processIds: filters.processes?.map(p => p.processId),
          controlCategory: filters.controlCategory,
          controlType: filters.controlType,
          frequency: filters.frequency,
          isKeyControl: filters.keyOrNonKey === 'Key' ? true : filters.keyOrNonKey === 'Non-Key' ? false : undefined,
          riskRanking: filters.riskRanking,
          processOwnerIds: filters.processOwners?.map(o => o.userId),
          testingScope: filters.testingScope,
          designEffective: filters.designEffective?.map(v => convertEffectiveness(v)),
          interimCompleted: filters.interimCompleted,
          finalCompleted: filters.finalCompleted,
          operatingEffectiveness: filters.operatingEffectiveness?.map(v => convertEffectiveness(v)),
          completionDate: filters.completionDate ?? undefined,
          effectivenessStatus: filters.effectivenessStatus,
          hasAuditorAssigned: filters.hasAuditorAssigned,
        });

        dispatch(setControlActivities({
          data: response.data,
          totalCount: response.totalCount,
          reportId: selectedReport.annualReportId
        }));

        console.warn(`✅ Fetched ${response.data.length} control activities`);
      } catch (error) {
        console.error('Error fetching control activities:', error);
        message.error('Failed to load control activities');
      } finally {
        setIsLoading(false);
        setIsSortLoading(false);
        setIsFilterLoading(false);
        isFetchingRef.current = false;
      }
    };

    fetchData();
  }, [selectedReport, dispatch, sortedParam, filters]);

  // Check if any drawer filters are active
  const hasActiveFilters = useMemo(() => {
    return !!(
      filters.status?.length ||
      filters.cycles?.length ||
      filters.processes?.length ||
      filters.controlCategory?.length ||
      filters.controlType?.length ||
      filters.frequency?.length ||
      filters.keyOrNonKey ||
      filters.riskRanking?.length ||
      filters.processOwners?.length ||
      (filters.testingScope !== null && filters.testingScope !== undefined) ||
      filters.designEffective?.length ||
      filters.interimCompleted?.length ||
      filters.finalCompleted?.length ||
      filters.operatingEffectiveness?.length ||
      filters.completionDate ||
      (filters.effectivenessStatus && filters.effectivenessStatus.length > 0) ||
      (filters.hasAuditorAssigned !== null && filters.hasAuditorAssigned !== undefined)
    );
  }, [filters]);

  // Filtered data comes directly from API (backend filtering)
  const filteredData = controlActivities;

  // Calculate paginated data from filtered data
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return filteredData.slice(start, end);
  }, [filteredData, currentPage, pageSize]);

  const filteredTotalCount = hasActiveFilters ? filteredData.length : totalCount;
  const totalPages = Math.ceil(filteredTotalCount / pageSize);

  const handleNavigateToCycle = (cycleId: string) => {
    navigate('/process-hierarchy', { state: { selectedCycleId: cycleId } });
  };

  const handleNavigateToProcess = (processId: string) => {
    navigate('/process-hierarchy', { state: { selectedProcessId: processId } });
  };

  const handleNavigateToControl = (control: ControlActivity) => {
    if (!isExternalUser)
      navigate(`/control-activities/${control.id}`);
  };

  const handleNavigateToApplication = (appId: string) => {
    navigate('/audit-plan', { state: { selectedApplicationId: appId } });
  };

  // Open Test Plan drawer
  const handleOpenTestPlan = (controlId: string, content: string) => {
    setTestPlanControlId(controlId);
    setTestPlanContent(content || '');
    setTestPlanDrawerOpen(true);
  };

  // Close Test Plan drawer
  const handleCloseTestPlan = () => {
    setTestPlanDrawerOpen(false);
    setTestPlanContent('');
    setTestPlanControlId(null);
  };

  // Save Test Plan
  const handleSaveTestPlan = async () => {
    if (!testPlanControlId) return;

    const control = controlActivities.find(c => c.id === testPlanControlId);
    if (!control) {
      message.error('Control not found');
      return;
    }

    setIsUpdatingTestPlan(true);

    try {
      const request = buildUpdateControlRequest(control, { testPlan: testPlanContent });
      await updateControl(request);
      await reloadData();
      message.success('Test Plan saved successfully');
      handleCloseTestPlan();
    } catch (error: any) {
      console.error('Failed to save test plan:', error);
      message.error(error.message || 'Failed to save Test Plan');
    } finally {
      setIsUpdatingTestPlan(false);
    }
  };

  // Open Walkthrough Result drawer
  const handleOpenWalkthrough = (controlId: string, content: string) => {
    setWalkthroughControlId(controlId);
    setWalkthroughContent(content || '');
    setWalkthroughDrawerOpen(true);
  };

  // Close Walkthrough Result drawer
  const handleCloseWalkthrough = () => {
    setWalkthroughDrawerOpen(false);
    setWalkthroughContent('');
    setWalkthroughControlId(null);
  };

  // Save Walkthrough Result
  const handleSaveWalkthrough = async () => {
    if (!walkthroughControlId) return;

    const control = controlActivities.find(c => c.id === walkthroughControlId);
    if (!control) {
      message.error('Control not found');
      return;
    }

    setIsUpdatingWalkthrough(true);

    try {
      const request = buildUpdateControlRequest(control, { walthroughResult: walkthroughContent });
      await updateControl(request);
      await reloadData();
      message.success('Walkthrough Result saved successfully');
      handleCloseWalkthrough();
    } catch (error: any) {
      console.error('Failed to save walkthrough result:', error);
      message.error(error.message || 'Failed to save Walkthrough Result');
    } finally {
      setIsUpdatingWalkthrough(false);
    }
  };

  // Toggle expanded state for a row
  const toggleRowExpand = (controlId: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(controlId)) {
        newSet.delete(controlId);
      } else {
        newSet.add(controlId);
      }
      return newSet;
    });
  };

  // Check if any row is expanded
  const hasExpandedRows = expandedRows.size > 0;

  // Define main columns (always visible)
  const mainColumns: ColumnsType<ControlActivity> = [
    {
      title: () => (
        <SortableColumnHeader
          title="Control ID"
          field="ControlCode"
          sortState={sortState}
          onSort={handleSort}
        />
      ),
      dataIndex: 'controlId',
      key: 'controlId',
      width: 120,
      fixed: 'left',
      render: (_, record) => (
        <ControlIdChip onClick={() => handleNavigateToControl(record)}>
          {record.controlCode}
        </ControlIdChip>
      ),
    },
    {
      title: () => (
        <SortableColumnHeader
          title="Status"
          field="ControlStatusRaw"
          sortState={sortState}
          onSort={handleSort}
        />
      ),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => (
        <StatusChip $status={status}>{status}</StatusChip>
      ),
    },
    {
      title: () => (
        <SortableColumnHeader
          title="Cycle"
          field="CycleCode"
          sortState={sortState}
          onSort={handleSort}
        />
      ),
      dataIndex: 'cycle',
      key: 'cycle',
      width: 140,
      render: (_, record) => (
        <Tooltip
          title={
            <div>
              <TooltipTitle>Cycle</TooltipTitle>
              <TooltipList>
                <TooltipItem onClick={() => handleNavigateToCycle(record.cycle.id)}>
                  {record.cycle.code} {record.cycle.name}
                </TooltipItem>
              </TooltipList>
            </div>
          }
          placement="top"
          overlayStyle={{ maxWidth: '400px' }}
          overlayInnerStyle={{
            backgroundColor: '#FFFFFF',
            padding: '16px',
            borderRadius: '12px',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
          }}
          color="#FFFFFF"
        >
          <CycleChip onClick={() => handleNavigateToCycle(record.cycle.id)}>
            {record.cycle.code} {record.cycle.name}
          </CycleChip>
        </Tooltip>
      ),
    },
    {
      title: () => (
        <SortableColumnHeader
          title="Process"
          field="ProcessCode"
          sortState={sortState}
          onSort={handleSort}
        />
      ),
      dataIndex: 'process',
      key: 'process',
      width: 140,
      render: (_, record) => (
        <Tooltip
          title={
            <div>
              <TooltipTitle>Process</TooltipTitle>
              <TooltipList>
                <TooltipItem onClick={() => handleNavigateToProcess(record.process.id)}>
                  {record.process.code} {record.process.name}
                </TooltipItem>
              </TooltipList>
            </div>
          }
          placement="top"
          overlayStyle={{ maxWidth: '400px' }}
          overlayInnerStyle={{
            backgroundColor: '#FFFFFF',
            padding: '16px',
            borderRadius: '12px',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
          }}
          color="#FFFFFF"
        >
          <ProcessChip onClick={() => handleNavigateToProcess(record.process.id)}>
            {record.process.code} {record.process.name}
          </ProcessChip>
        </Tooltip>
      ),
    },
    {
      title: 'Control Description',
      dataIndex: 'controlDescription',
      key: 'controlDescription',
      width: 280,
      render: (_, record) => (
        <DescriptionText>
          <span className="what-label">What:</span>{' '}
          <span className="what-text">{record.controlDescription.what}</span>
          <div className="when-text">When... Who... Why... How... Evidence...</div>
        </DescriptionText>
      ),
    },
    {
      title: 'Control Category',
      dataIndex: 'controlCategory',
      key: 'controlCategory',
      width: 150,
      render: (category: string) => (
        <GrayChip>{category}</GrayChip>
      ),
    },
    {
      title: 'Control Type',
      dataIndex: 'controlType',
      key: 'controlType',
      width: 150,
      render: (type: string) => (
        <GrayChip>{type}</GrayChip>
      ),
    },
    {
      title: () => (
        <SortableColumnHeader
          title="Frequency"
          field="FrequencyRaw"
          sortState={sortState}
          onSort={handleSort}
        />
      ),
      dataIndex: 'frequency',
      key: 'frequency',
      width: 120,
      render: (frequency: string) => (
        <GrayChip>{frequency}</GrayChip>
      ),
    },
    {
      title: () => (
        <SortableColumnHeader
          title="Key or Non-Key"
          field="IsKeyControl"
          sortState={sortState}
          onSort={handleSort}
        />
      ),
      dataIndex: 'keyOrNonKey',
      key: 'keyOrNonKey',
      width: 150,
      render: (keyOrNonKey: string) => (
        <GrayChip>{keyOrNonKey}</GrayChip>
      ),
    },
    {
      title: () => (
        <SortableColumnHeader
          title="Risk Ranking"
          field="RiskRatingRaw"
          sortState={sortState}
          onSort={handleSort}
        />
      ),
      dataIndex: 'riskRanking',
      key: 'riskRanking',
      width: 150,
      render: (riskRanking: string, record) => {
        const objectiveDisplayTitles = record.objectiveDisplayTitles || [];
        const objectiveIds = record.objectiveIds || [];
        
        // If no objectives, just show the chip without tooltip
        if (objectiveDisplayTitles.length === 0) {
          return (
            <RiskRankingChip $level={riskRanking}>
              {riskRanking}
            </RiskRankingChip>
          );
        }

        // Tooltip content showing objectives
        const tooltipContent = (
          <div>
            <RiskTooltipTitle>Linked Objectives</RiskTooltipTitle>
            <RiskTooltipList>
              {objectiveDisplayTitles.map((objNum, index) => {
                // Extract process code from objective number (e.g., "A01.01" → "A01")
                const processCode = objNum.split('.')[0];
                
                return (
                  <RiskTooltipItem
                    key={objectiveIds[index] || index}
                    onClick={() => {
                      // Navigate to EX4 Tab 2 using processCode
                      // RiskAssessmentDetailPage will resolve processCode to assessmentId
                      navigate(`/risk-assessment/${processCode}/tab2`);
                    }}
                  >
                    {objNum}
                  </RiskTooltipItem>
                );
              })}
            </RiskTooltipList>
          </div>
        );

        return (
          <Tooltip
            title={tooltipContent}
            placement="top"
            overlayStyle={{ maxWidth: '300px' }}
            overlayInnerStyle={{
              backgroundColor: '#FFFFFF',
              padding: '16px',
              borderRadius: '12px',
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
            }}
            color="#FFFFFF"
          >
            <RiskRankingChip $level={riskRanking}>
              {riskRanking}
            </RiskRankingChip>
          </Tooltip>
        );
      },
    },
    {
      title: 'Control Owner',
      dataIndex: 'controlOwner',
      key: 'controlOwner',
      width: 150,
      render: (controlOwner: string) => (
        <ControlOwnerText>{controlOwner}</ControlOwnerText>
      ),
    },
    {
      title: 'Process Owner',
      dataIndex: 'processOwner',
      key: 'processOwner',
      width: 180,
      render: (processOwner: { name: string; avatar?: string }) => (
        <ProcessOwnerContainer>
          <ProcessOwnerAvatar>
            {processOwner.avatar || processOwner.name.charAt(0)}
          </ProcessOwnerAvatar>
          <ProcessOwnerName>{processOwner.name}</ProcessOwnerName>
        </ProcessOwnerContainer>
      ),
    },
    {
      title: 'Applications',
      dataIndex: 'applications',
      key: 'applications',
      width: 180,
      render: (applications: { id: string; name: string }[]) => {
        if (!applications || applications.length === 0) {
          return <span style={{ color: '#9CA3AF' }}>-</span>;
        }

        const tooltipContent = (
          <div>
            <RiskTooltipTitle>Applications</RiskTooltipTitle>
            <RiskTooltipList>
              {applications.map((app) => (
                <RiskTooltipItem
                  key={app.id}
                  onClick={() => handleNavigateToApplication(app.id)}
                >
                  {app.name}
                </RiskTooltipItem>
              ))}
            </RiskTooltipList>
          </div>
        );

        const chipContent = (
          <div style={{ display: 'flex', flexWrap: 'nowrap', gap: '4px', overflow: 'hidden' }}>
            {applications.map((app) => (
              <ApplicationChip
                key={app.id}
                onClick={() => handleNavigateToApplication(app.id)}
              >
                {app.name}
              </ApplicationChip>
            ))}
          </div>
        );

        return (
          <Tooltip
            title={tooltipContent}
            placement="top"
            overlayStyle={{ maxWidth: '400px' }}
            overlayInnerStyle={{
              backgroundColor: '#FFFFFF',
              padding: '16px',
              borderRadius: '12px',
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
            }}
            color="#FFFFFF"
          >
            {chipContent}
          </Tooltip>
        );
      },
    },
    {
      title: 'Control Weight',
      dataIndex: 'controlWeight',
      key: 'controlWeight',
      width: 120,
      render: (controlWeight: number | null) => (
        <span style={{ color: controlWeight ? '#1F2937' : '#9CA3AF' }}>
          {(controlWeight ?? 0) > 0 ? controlWeight : '-'}
        </span>
      ),
    },
    {
      title: 'Assertions',
      dataIndex: 'assertions',
      key: 'assertions',
      width: 200,
      render: (assertions: string[], record) => {
        const objectiveDisplayTitles = record.objectiveDisplayTitles || [];
        const objectiveIds = record.objectiveIds || [];
        if (!assertions || assertions.length === 0) {
          return null;
        }

        // Tooltip content showing objectives
        const tooltipContent = (
          <div>
            <RiskTooltipTitle>Linked Objectives</RiskTooltipTitle>
            <RiskTooltipList>
              {objectiveDisplayTitles.map((objNum, index) => {
                return (
                  <RiskTooltipItem
                    key={objectiveIds[index] || index}
                    onClick={() => {
                      // Navigate to EX4 Tab 2 using processCode
                      // RiskAssessmentDetailPage will resolve processCode to assessmentId
                      // navigate(`/risk-assessment/${processCode}/tab2`);
                    }}
                  >
                    {objNum}
                  </RiskTooltipItem>
                );
              })}
            </RiskTooltipList>
          </div>
        );

        return (
          <Tooltip
            title={tooltipContent}
            placement="top"
            overlayStyle={{ maxWidth: '400px' }}
            overlayInnerStyle={{
              backgroundColor: '#FFFFFF',
              padding: '16px',
              borderRadius: '12px',
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
            }}
            color="#FFFFFF"
          >
            <div style={{ display: 'flex', flexWrap: 'nowrap', gap: '4px', overflow: 'hidden' }}>
            {assertions.map((assertion, idx) => (
              <AssertionChip key={idx}>
                {assertion}
              </AssertionChip>
            ))}
          </div>
          </Tooltip>
        );
      },
    },
    {
      title: () => (
        <SortableColumnHeader
          title="Testing Scope"
          field="IsInTestingScope"
          sortState={sortState}
          onSort={handleSort}
        />
      ),
      dataIndex: 'testingScope',
      key: 'testingScope',
      width: 150,
      render: (testingScope: boolean, record) => {
        const isExpanded = expandedRows.has(record.id);
        return (
          <TestingScopeChip
            $inScope={testingScope}
            $expanded={isExpanded}
            $clickable={testingScope}
            onClick={() => testingScope && toggleRowExpand(record.id)}
          >
            {testingScope && (
              <ExpandIcon $expanded={isExpanded}>▶</ExpandIcon>
            )}
            {testingScope ? 'In Scope' : 'Out of Scope'}
          </TestingScopeChip>
        );
      },
    },
  ];

  // Define testing columns (only visible when expanded)
  const testingColumns: ColumnsType<ControlActivity> = [
    {
      title: 'Test Plan',
      dataIndex: ['testing', 'testPlan'],
      key: 'testPlan',
      width: 180,
      render: (testPlan: string, record) => {
        const isOutOfScope = !record.testingScope;
        if (isOutOfScope || !testPlan) return <span style={{ opacity: 0.5 }}>-</span>;
        return (
          <TruncatedText
            $maxWidth="160px"
            onClick={() => handleOpenTestPlan(record.id, testPlan)}
          >
            {testPlan}
          </TruncatedText>
        );
      },
    },
    {
      title: 'Walkthrough (WT) Result',
      dataIndex: ['testing', 'walkthroughResult'],
      key: 'walkthroughResult',
      width: 200,
      render: (result: string, record) => {
        const isOutOfScope = !record.testingScope;
        if (isOutOfScope || !result) return <span style={{ opacity: 0.5 }}>-</span>;
        return (
          <TruncatedText
            $maxWidth="180px"
            onClick={() => handleOpenWalkthrough(record.id, result)}
          >
            {result}
          </TruncatedText>
        );
      },
    },
    {
      title: () => (
        <SortableColumnHeader
          title="Design Effective"
          field="DesignEffective"
          sortState={sortState}
          onSort={handleSort}
        />
      ),
      dataIndex: ['testing', 'designEffective'],
      key: 'designEffective',
      width: 160,
      render: (value: string, record) => {
        const isOutOfScope = !record.testingScope;
        if (isOutOfScope) return <span style={{ opacity: 0.5 }}>-</span>;
        return <DesignEffectiveChip $value={value || 'WIP'}>{value || 'WIP'}</DesignEffectiveChip>;
      },
    },
    {
      title: 'Total Test Samples',
      dataIndex: ['testing', 'totalTestSamples'],
      key: 'totalTestSamples',
      width: 140,
      align: 'center',
      render: (value: number, record) => {
        const isOutOfScope = !record.testingScope;
        if (isOutOfScope) return <span style={{ opacity: 0.5 }}>-</span>;
        return value ?? 0;
      },
    },
    {
      title: 'Samples Tested',
      dataIndex: ['testing', 'samplesTested'],
      key: 'samplesTested',
      width: 130,
      align: 'center',
      render: (value: number, record) => {
        const isOutOfScope = !record.testingScope;
        if (isOutOfScope) return <span style={{ opacity: 0.5 }}>-</span>;
        return value ?? 0;
      },
    },
    {
      title: 'WT & Test Sheet',
      dataIndex: ['testing', 'attachments'],
      key: 'attachments',
      width: 180,
      render: (attachments: { name: string; id: string }[] | undefined, record) => {
        const isOutOfScope = !record.testingScope;
        if (isOutOfScope || !attachments?.length) return <span style={{ opacity: 0.5 }}>-</span>;
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            {attachments.slice(0, 3).map((file, idx) => (
              <AttachmentLinkWithDownload
                key={idx}
                fileId={file.id}
                fileName={file.name}
              />
            ))}
          </div>
        );
      },
    },
    {
      title: 'Testing Status',
      dataIndex: ['testing'],
      key: 'testingStatus',
      width: 120,
      align: 'center',
      render: (testing: ControlActivity['testing'], record) => {
        const isOutOfScope = !record.testingScope;
        if (isOutOfScope || !testing) return <span style={{ opacity: 0.5 }}>-</span>;

        const total = testing.totalTestSamples ?? 0;
        const tested = testing.samplesTested ?? 0;

        // US 5: Testing Status % Calculation (read-only)
        // If Total = 0 AND Tested = 0 → display "N/A"
        // If Total = 0 (edge case) → display "N/A" to avoid division by zero
        // Else → (Samples Tested / Total Samples) * 100, rounded to nearest integer
        if (total === 0 && tested === 0) {
          return 'N/A';
        }
        if (total === 0) {
          return 'N/A'; // Avoid division by zero
        }

        const percentage = ((tested / total) * 100).toFixed(2);
        return `${percentage}%`;
      },
    },
    {
      title: () => (
        <SortableColumnHeader
          title="Interim Completed"
          field="InterimCompletedRaw"
          sortState={sortState}
          onSort={handleSort}
        />
      ),
      dataIndex: ['testing', 'interimCompleted'],
      key: 'interimCompleted',
      width: 180,
      render: (value: string, record) => {
        const isOutOfScope = !record.testingScope;
        if (isOutOfScope) return <span style={{ opacity: 0.5 }}>-</span>;
        return <CompletionChip $value={value || 'Not Started'}>{value || 'Not Started'}</CompletionChip>;
      },
    },
    {
      title: () => (
        <SortableColumnHeader
          title="Final Completed"
          field="FinalCompletedRaw"
          sortState={sortState}
          onSort={handleSort}
        />
      ),
      dataIndex: ['testing'],
      key: 'finalCompleted',
      width: 160,
      render: (testing: ControlActivity['testing'], record) => {
        const isOutOfScope = !record.testingScope;
        if (isOutOfScope || !testing) return <span style={{ opacity: 0.5 }}>-</span>;

        const total = testing.totalTestSamples ?? 0;
        const tested = testing.samplesTested ?? 0;

        // US 6: Computed Final Completed Status (read-only)
        // Based on Testing Status (Samples Tested / Total Samples)
        // If Total = 0 AND Tested = 0 → display "N/A"
        if (total === 0 && tested === 0) {
          return <CompletionChip $value="N/A">N/A</CompletionChip>;
        }

        // Avoid division by zero
        if (total === 0) {
          return <CompletionChip $value="N/A">N/A</CompletionChip>;
        }

        const testingStatus = Math.round((tested / total) * 100);

        // Not Started: Testing Status = 0 (Red)
        // WIP: 0 < Testing Status < 100 (Yellow)
        // Completed: Testing Status = 100 (Green)
        let status: string;
        if (testingStatus === 0) {
          status = 'Not Started';
        } else if (testingStatus < 100) {
          status = 'WIP';
        } else {
          status = 'Completed';
        }

        return <CompletionChip $value={status}>{status}</CompletionChip>;
      },
    },
    {
      title: () => (
        <SortableColumnHeader
          title="Operating Effectiveness"
          field="OperatingEffectiveness"
          sortState={sortState}
          onSort={handleSort}
        />
      ),
      dataIndex: ['testing', 'operatingEffectiveness'],
      key: 'operatingEffectiveness',
      width: 210,
      render: (value: string, record) => {
        const isOutOfScope = !record.testingScope;
        if (isOutOfScope) return <span style={{ opacity: 0.5 }}>-</span>;
        return <DesignEffectiveChip $value={value || 'WIP'}>{value || 'WIP'}</DesignEffectiveChip>;
      },
    },
    {
      title: 'Completion Date',
      dataIndex: ['testing', 'completionDate'],
      key: 'completionDate',
      width: 130,
      render: (value: string, record) => {
        const isOutOfScope = !record.testingScope;
        if (isOutOfScope || !value) return <span style={{ opacity: 0.5 }}>-</span>;
        return new Date(value).toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: '2-digit'
        });
      },
    },
    {
      title: 'Auditor Assigned',
      dataIndex: ['testing', 'auditorAssigned'],
      key: 'auditorAssigned',
      width: 220,
      render: (auditor: { name: string; avatar?: string } | undefined, record) => {
        const isOutOfScope = !record.testingScope;
        if (isOutOfScope || !auditor) return <span style={{ opacity: 0.5 }}>-</span>;
        return (
          <ProcessOwnerContainer>
            <ProcessOwnerAvatar>
              {auditor.avatar || auditor.name.charAt(0)}
            </ProcessOwnerAvatar>
            <ProcessOwnerName>{auditor.name}</ProcessOwnerName>
          </ProcessOwnerContainer>
        );
      },
    },
    {
      title: 'Budget Hours',
      dataIndex: ['testing', 'budgetHoursToTest'],
      key: 'budgetHours',
      width: 120,
      align: 'center',
      render: (value: number, record) => {
        const isOutOfScope = !record.testingScope;
        if (isOutOfScope) return <span style={{ opacity: 0.5 }}>-</span>;
        return value ?? '-';
      },
    },
  ];

  // Action column (always visible at the end)
  const actionColumn: ColumnsType<ControlActivity> = [
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      fixed: 'right',
      align: 'center',
      render: (_, record) => {
        const isExpanded = expandedRows.has(record.id);
        const hasTestingScope = record.testingScope;

        // Check delete blockers for Active controls
        const isActiveControl = record.status === 'Active';
        const isFinalCompleted = record.testing?.finalCompleted === 'Completed';
        
        // TODO: After EX6 implementation, add GAP link check
        // const hasActiveGap = record.linkedGap?.status === 'Active' || record.linkedGap?.status === 'Archived';
        const hasActiveGap = false; // TODO: Check GAP link after EX6
        
        // Determine if delete should be disabled
        const canDelete = isAdmin && (!isActiveControl || (!isFinalCompleted && !hasActiveGap));
        
        // Tooltip for disabled delete
        let deleteTooltip = '';
        if (!isAdmin) {
          deleteTooltip = 'Admin access required';
        } else if (isFinalCompleted) {
          deleteTooltip = 'Cannot delete a control that has been finalized';
        } else if (hasActiveGap) {
          deleteTooltip = 'Cannot delete a control with an active or archived GAP assigned';
        }

        const menuItems: MenuProps['items'] = [
          {
            key: 'toggle-testing',
            label: (
              <MenuItemContent style={{ opacity: !hasTestingScope ? 0.5 : 1 }}>
                {isExpanded ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                <MenuItemText>
                  {isExpanded ? 'Hide Testing Fields' : 'Show Testing Fields'}
                </MenuItemText>
              </MenuItemContent>
            ),
            onClick: () => toggleRowExpand(record.id),
            disabled: !hasTestingScope,
          },
          
        ];

        if (!isExternalUser) { 
          menuItems.push(
            {
            key: 'edit',
            label: (
              <MenuItemContent>
                <EditIcon />
                <MenuItemText>Edit</MenuItemText>
              </MenuItemContent>
            ),
            onClick: () => handleEditControl(record),
          }
          )
        }

        // Only add delete option if user is admin
        if (isAdmin) {
          menuItems.push(
            {
              type: 'divider',
            },
            {
              key: 'delete',
              label: deleteTooltip ? (
                <Tooltip title={deleteTooltip} placement="left">
                  <MenuItemContent style={{ opacity: !canDelete ? 0.5 : 1 }}>
                    <TrashIcon />
                    <MenuItemText $danger>Delete</MenuItemText>
                  </MenuItemContent>
                </Tooltip>
              ) : (
                <MenuItemContent>
                  <TrashIcon />
                  <MenuItemText $danger>Delete</MenuItemText>
                </MenuItemContent>
              ),
              onClick: () => handleDeleteControl(record),
              disabled: !canDelete,
            }
          );
        }

        const getMenuItems = (): MenuProps['items'] => menuItems;

        const handleActionButtonClick = (e: React.MouseEvent) => {
          e.stopPropagation();
        };

        return (
          <div onClick={handleActionButtonClick}>
            <Dropdown
              menu={{ items: getMenuItems() }}
              trigger={['click']}
              placement="bottomRight"
            >
              <ActionMenuButton >
                <MoreOutlined />
              </ActionMenuButton>
            </Dropdown>
          </div>
        );
      },
    },
  ];

  // Combine columns based on expanded state and filter Budget Hours for non-admin
  // Include sortState and isSortLoading to ensure columns update when sort changes
  const columns = useMemo(() => {
    let baseColumns = mainColumns;
    
    if (hasExpandedRows) {
      // Filter out Budget Hours column if user is not admin
      const filteredTestingColumns = isAdmin
        ? testingColumns
        : testingColumns.filter(col => col.key !== 'budgetHours');
      baseColumns = [...mainColumns, ...filteredTestingColumns];
    }
    
    // Always add action column at the end
    return [...baseColumns, ...actionColumn];
  }, [hasExpandedRows, expandedRows, isAdmin, mainColumns, testingColumns, actionColumn, sortState]);

  // Handle page change
  const handlePageChange = (page: number) => {
    dispatch(setCurrentPage(page));
  };

  // Handle Add Control button click
  const handleAddControl = () => {
    navigate('/control-activities/create');
  };

  // Handle successful control creation - refresh the list
  const reloadData = useCallback(async () => {
    if (!selectedReport?.annualReportId) return;

    try {
      const response = await getControlActivities({
        annualReportId: selectedReport.annualReportId,
        status: filters.status,
        cycleIds: filters.cycles?.map(c => c.cycleId),
        processIds: filters.processes?.map(p => p.processId),
        controlCategory: filters.controlCategory,
        controlType: filters.controlType,
        frequency: filters.frequency,
        isKeyControl: filters.keyOrNonKey === 'Key' ? true : filters.keyOrNonKey === 'Non-Key' ? false : undefined,
        riskRanking: filters.riskRanking,
        processOwnerIds: filters.processOwners?.map(o => o.userId),
        testingScope: filters.testingScope,
        designEffective: filters.designEffective?.map(v => convertEffectiveness(v)),
        interimCompleted: filters.interimCompleted,
        finalCompleted: filters.finalCompleted,
        operatingEffectiveness: filters.operatingEffectiveness?.map(v => convertEffectiveness(v)),
        completionDate: filters.completionDate ?? undefined,
        effectivenessStatus: filters.effectivenessStatus,
        hasAuditorAssigned: filters.hasAuditorAssigned,
      });
      dispatch(setControlActivities({
        data: response.data,
        totalCount: response.totalCount,
        reportId: selectedReport.annualReportId
      }));
    } catch (error) {
      console.error('Error refreshing control activities:', error);
    }
  }, [selectedReport, dispatch, filters]);

  // Action handlers
  const handleEditControl = (control: ControlActivity) => {
    handleNavigateToControl(control);
  };

  const handleDeleteControl = (control: ControlActivity) => {
    setDeletingControl(control);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingControl) {
      message.error('Control ID is required for delete');
      return;
    }

    setIsDeleting(true);
    try {
      await deleteControl(deletingControl.id);
      await reloadData();
      // Clear hierarchy cache so EX3 control counts refresh
      dispatch(clearHierarchyData());
      setDeleteModalOpen(false);
      setDeletingControl(null);
      message.success('Control Activity deleted successfully');
    } catch (error: any) {
      console.error('Failed to delete control:', error);
      message.error(error.message || 'Failed to delete Control Activity');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setDeletingControl(null);
  };

  // Loading state (prioritize over empty state on initial load)
  if (isLoading) {
    return (
      <PageContainer>
        <PageHeader>
          <PageTitle level={2}>Control Activities</PageTitle>
        </PageHeader>
        <div style={{ position: 'relative', minHeight: '400px' }}>
          <LoadingOverlay>
            <LoadingSpinner>
              <div className="spinner" />
              <div className="text">Loading control activities...</div>
            </LoadingSpinner>
          </LoadingOverlay>
        </div>
      </PageContainer>
    );
  }

  // Empty state - only show if we've attempted to fetch (not initial load)
  if (!isLoading && controlActivities.length === 0 && lastFetchedReportId !== null) {
    return (
      <PageContainer>
        <PageHeader>
          <PageTitle level={2}>Control Activities</PageTitle>
        </PageHeader>
        <TableContainer>
          <EmptyStateContainer>
            {hasActiveFilters ? (
              <>
                <EmptyStateMessage>No results found for the current filters.</EmptyStateMessage>
                <AddItemButton 
                  text="Update Filters" 
                  onClick={() => setFilterDrawerOpen(true)} 
                />
              </>
            ) : (
              <>
                <EmptyStateMessage>There are no controls to display yet.</EmptyStateMessage>
                {!isExternalUser ? <AddItemButton text="Add Control" onClick={handleAddControl} /> : ""}
              </>
            )}
          </EmptyStateContainer>
        </TableContainer>
        
        {/* Keep filter drawer accessible in empty state */}
        <ControlsFilterDrawer
          open={filterDrawerOpen}
          onClose={() => setFilterDrawerOpen(false)}
          onApply={(filters) => {
            setFilters(filters);
            dispatch(setCurrentPage(1));
            setFilterDrawerOpen(false);
          }}
          currentFilters={filters}
          annualReportId={selectedReport?.annualReportId || null}
        />
      </PageContainer>
    );
  }

  // Show loading while waiting for initial load (before first fetch)
  if (controlActivities.length === 0 && lastFetchedReportId === null) {
    return (
      <PageContainer>
        <PageHeader>
          <PageTitle level={2}>Control Activities</PageTitle>
        </PageHeader>
        <div style={{ position: 'relative', minHeight: '400px' }}>
          <LoadingOverlay>
            <LoadingSpinner>
              <div className="spinner" />
              <div className="text">Loading control activities...</div>
            </LoadingSpinner>
          </LoadingOverlay>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle level={2}>Control Activities</PageTitle>
        <HeaderActions>
          {!isExternalUser ? <AddItemButton text="Add Control" onClick={handleAddControl} /> : ""}
          <Button
            type="default"
            icon={<SettingOutlined />}
            disabled={!isAdmin}
            onClick={() => navigate('/control-activities/manage-weights')}
            style={{ height: 40 }}
          >
            Manage Weights
          </Button>
          <ExportButton onClick={() => setExportPanelOpen(true)} />
          {selectedReport && isAdmin && (
            <ChangeLogButton
              actionType="control"
              annualReportId={selectedReport.annualReportId}
            />
          )}
          {/* Filter drawer toggle button - at the end */}
          <FilterButton
            onClick={() => setFilterDrawerOpen(true)}
            hasActiveFilters={hasActiveFilters}
          />
        </HeaderActions>
      </PageHeader>

      <TableWrapper>
        {(isSortLoading || isFilterLoading) && (
          <LoadingOverlay>
            <LoadingOutlined style={{ fontSize: 32, color: '#3B82F6' }} spin />
          </LoadingOverlay>
        )}
        <TableContainer>
          <Table
            columns={columns}
            dataSource={paginatedData}
            rowKey="id"
            pagination={false}
            scroll={{ x: hasExpandedRows ? 4580 : 2880, y: 'calc(100vh - 260px)' }}
            size="middle"
          />

          <PaginationContainer>
            <Pagination
              current={currentPage}
              total={filteredTotalCount}
              pageSize={pageSize}
              onChange={handlePageChange}
              showSizeChanger
              pageSizeOptions={['10', '25', '50', '100']}
              onShowSizeChange={(_current, size) => dispatch(setGlobalPageSize(size))}
              showTotal={() => `${currentPage} of ${totalPages}`}
            />
          </PaginationContainer>
        </TableContainer>
      </TableWrapper>

      {/* Test Plan Drawer */}
      <RightContentDrawer
        title="Test Plan"
        width={480}
        open={testPlanDrawerOpen}
        onClose={handleCloseTestPlan}
        maskClosable={true}
        footer={
          <ButtonGroup>
            <Button type="default" onClick={handleCloseTestPlan} disabled={isUpdatingTestPlan}>
              Go back
            </Button>
            <Button type="primary" onClick={handleSaveTestPlan} loading={isUpdatingTestPlan} disabled={!isAdmin}>
              Save
            </Button>
          </ButtonGroup>
        }
      >
        <DrawerContent>
          <DrawerTextArea
            value={testPlanContent}
            onChange={(e) => setTestPlanContent(e.target.value)}
            placeholder="Enter test plan details..."
          />
        </DrawerContent>
      </RightContentDrawer>

      {/* Walkthrough Result Drawer */}
      <RightContentDrawer
        title="Walkthrough (WT) Result"
        width={480}
        open={walkthroughDrawerOpen}
        onClose={handleCloseWalkthrough}
        maskClosable={true}
        footer={
          <ButtonGroup>
            <Button type="default" onClick={handleCloseWalkthrough} disabled={isUpdatingWalkthrough}>
              Go back
            </Button>
            <Button type="primary" onClick={handleSaveWalkthrough} loading={isUpdatingWalkthrough} disabled={!isAdmin}>
              Save
            </Button>
          </ButtonGroup>
        }
      >
        <DrawerContent>
          <DrawerTextArea
            value={walkthroughContent}
            onChange={(e) => setWalkthroughContent(e.target.value)}
            placeholder="Enter walkthrough result details..."
          />
        </DrawerContent>
      </RightContentDrawer>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        open={deleteModalOpen}
        title={
          deletingControl?.status === 'Draft' 
            ? 'Delete Draft Control' 
            : 'Delete Active Control'
        }
        description={
          deletingControl ? (
            deletingControl.status === 'Draft' ? (
              // Draft control - standard confirmation
              `Are you sure you want to delete control "${deletingControl.controlCode}"?`
            ) : (
              // Active control - high warning
              <div style={{ textAlign: 'left' }}>
                <div style={{ color: '#DC2626', fontWeight: 600, marginBottom: '8px' }}>
                  ⚠️ Warning: You are deleting an Active control.
                </div>
                <div style={{ marginBottom: '12px' }}>
                  Control: <strong>{deletingControl.controlCode}</strong>
                </div>
                <div>
                  This will permanently remove all associated testing data, samples, and attachments.
                </div>
              </div>
            )
          ) : ''
        }
        icon={warningIcon}
        confirmText="Delete"
        cancelText="Cancel"
        loading={isDeleting}
        onConfirm={handleConfirmDelete}
        onClose={handleCancelDelete}
        danger
      />

      {/* Export Panel */}
      <ExportPanel
        open={exportPanelOpen}
        onClose={() => setExportPanelOpen(false)}
        data={exportData}
        columns={exportColumns}
        fileName="ControlActivities"
        title="Control Activities"
      />

      {/* Filter Drawer */}
      <ControlsFilterDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        onApply={(filters) => {
          setFilters(filters);
          dispatch(setCurrentPage(1)); // Reset to first page when filters change
          setFilterDrawerOpen(false); // Close drawer after applying
        }}
        currentFilters={filters}
        annualReportId={selectedReport?.annualReportId || null}
      />
    </PageContainer>
  );
};

export default ControlActivitiesPage;
